import { useState, useEffect, useCallback } from 'react';
import { Box, CircularProgress, Alert } from '@mui/material';
import { marketingApi } from './api';
import { getFirstOfMonth, getToday } from './utils';
import NavBar from './components/NavBar';
import AnalyticsFilters from './components/AnalyticsFilters';
import AttributionCards from './components/AttributionCards';
import SourceRevenueChart from './components/SourceRevenueChart';
import PaidOrganicPie from './components/PaidOrganicPie';
import CallsChart from './components/CallsChart';
import SourceTable from './components/SourceTable';

const AnalyticsPage = ({ onLogout, page, onPageChange }) => {
  const [dateFrom, setDateFrom] = useState(getFirstOfMonth());
  const [dateTo, setDateTo] = useState(getToday());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await marketingApi.getAttribution({ dateFrom, dateTo });
      setData(res.data);
    } catch (err) {
      console.error('Failed to load attribution:', err);
      setError(err.response?.data?.message || 'Failed to load marketing data');
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo]);

  useEffect(() => { loadData(); }, [loadData]);

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f0f2f5' }}>
      <NavBar page={page} onPageChange={onPageChange} onLogout={onLogout} onRefresh={loadData} loading={loading} />

      <Box sx={{ maxWidth: 1400, mx: 'auto', p: 3 }}>
        <AnalyticsFilters
          dateFrom={dateFrom} setDateFrom={setDateFrom}
          dateTo={dateTo} setDateTo={setDateTo}
          loading={loading} onApply={loadData}
        />

        {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

        {loading && !data ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
            <CircularProgress size={60} />
          </Box>
        ) : data ? (
          <>
            <AttributionCards data={data} />

            <Box sx={{ display: 'flex', gap: 3, mb: 3, flexWrap: 'wrap' }}>
              <Box sx={{ flex: '1 1 58%', minWidth: 400 }}>
                <SourceRevenueChart sources={data.sources} />
              </Box>
              <Box sx={{ flex: '1 1 38%', minWidth: 300 }}>
                <PaidOrganicPie totals={data.totals} />
              </Box>
            </Box>

            <CallsChart sources={data.sources} />
            <SourceTable sources={data.sources} totals={data.totals} attribution={data.attribution} />
          </>
        ) : null}
      </Box>
    </Box>
  );
};

export default AnalyticsPage;
